/**
 * Common values for metric pill inputs
 * These are pre-defined values that scouts commonly use for each metric
 */
export const COMMON_VALUES = {
  // Position Player - Velocities (mph)
  exitVelocity: [80, 85, 90, 95, 100],
  infieldVelocity: [75, 80, 85, 90, 95],
  outfieldVelocity: [80, 85, 90, 95, 100],
  catcherVelocity: [65, 70, 75, 80, 85],
  batSpeed: [65, 70, 75, 80, 85],

  // Position Player - Times (seconds)
  sixtyTime: [6.5, 6.8, 7.0, 7.2, 7.5],
  homeToFirst: [3.8, 4.0, 4.2, 4.4, 4.6],
  popTime: [1.8, 1.9, 2.0, 2.1, 2.2],

  // Position Player - Grip Strength (lbs)
  leftGripStrength: [80, 90, 100, 110, 120],
  rightGripStrength: [80, 90, 100, 110, 120],

  // Pitcher - Velocities (mph)
  fastballVelocity: [85, 88, 91, 94, 97],
  breakingBallVelocity: [70, 73, 76, 79, 82],
  changeupVelocity: [75, 78, 81, 84, 87],
  knuckleballVelocity: [60, 63, 66, 69, 72],
  splitterVelocity: [75, 78, 81, 84, 87],
  cutterVelocity: [82, 85, 88, 91, 94],

  // Softball Pitcher - Velocities (mph)
  //
  // Two bands cover all seven softball pitches, so these are named for the band rather than the
  // pitch: riseball, dropball, curveball, screwball and fastball all sit in 55-73, changeup and
  // off speed in 40-60. Six near-identical per-pitch keys were how the guidance drifted ~30mph
  // off the spec in the first place.
  softballVelocity55To73: [55, 59, 63, 68, 73],
  softballVelocity40To60: [40, 45, 50, 55, 60],

  // Softball Pitcher - Spin rate (rpm)
  spinRate: [700, 1200, 1700, 2200, 2800],
} as const;

/**
 * Units for different metric types
 */
export const METRIC_UNITS = {
  velocity: 'mph',
  time: 'sec',
  gripStrength: 'lbs',
  spinRate: 'rpm',
} as const;

/**
 * Validation ranges for different metric types
 */
export const METRIC_RANGES = {
  velocity: { min: 0, max: 150 },
  time: { min: 0, max: 20 },
  gripStrength: { min: 0, max: 200 },
  // Spin rate is the one metric whose range is a real constraint rather than a typo guard,
  // so its input is given `rejectOutOfRange` and refuses a reading outside these bounds with
  // a visible message instead of silently clamping to them. Widening the range, or dropping
  // the reject flag, would silently record a value the coach never measured.
  spinRate: { min: 700, max: 2800 },
  rating: { min: 0, max: 99 }, // 80/20 scale, but allow 0-99 for flexibility
} as const;

/**
 * Helper to get common values for a specific metric
 */
export function getCommonValues(metricName: keyof typeof COMMON_VALUES): readonly number[] {
  return COMMON_VALUES[metricName];
}

/**
 * Helper to get the unit for a metric type
 */
export function getMetricUnit(metricType: keyof typeof METRIC_UNITS): string {
  return METRIC_UNITS[metricType];
}

/**
 * Helper to get validation range for a metric type
 */
export function getMetricRange(metricType: keyof typeof METRIC_RANGES): { min: number; max: number } {
  return METRIC_RANGES[metricType];
}
